import React, { useState, useEffect, lazy, Suspense, useMemo } from 'react';
import Sidebar from './Sidebar';
import TopNav from './TopNav';
import { Outlet, useLocation } from 'react-router-dom';
import OnboardingWizard from './Onboarding/OnboardingWizard';
import TermsAgreementModal from './SuperAdmin/TermsAgreementModal';
import { getCurrentUser } from '../utils/auth';
import { usePlan } from '../contexts/PlanContext';

const AIDoubtChatWidget = lazy(() => import('./AIDoubtChat/AIDoubtChatWidget'));

const Layout = () => {
  const location = useLocation();
  const { hasPlanFeature } = usePlan();
  const isChatPage = location.pathname.endsWith('/chat');

  // Hide AIvo on live classroom and focus-critical learning flows
  const hideAIWidget = useMemo(() => {
    const path = location.pathname;
    return (
      path.startsWith('/s/classroom/') ||         // live Zoom classroom
      path.startsWith('/s/practice/') ||          // coding practice editor
      path.startsWith('/s/assessment/take/') ||    // taking a quiz/assessment
      path.startsWith('/s/mock-interview/') ||     // mock interview (setup, session, summary, history)
      path.startsWith('/s/learn-with-ai') ||       // learn with AI page
      path.startsWith('/s/learn-ai/')              // AI learning history
    );
  }, [location.pathname]);
  const [sidebarShow, setSidebarShow] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  useEffect(() => {
    const user = getCurrentUser();
    // Show terms modal for institute Super Admin who hasn't accepted
    if (user?.usertype === 'superadmin' && !user?.termsAccepted) {
      setShowTerms(true);
    }
    // Show onboarding wizard for superadmin users who haven't completed it
    if (user?.usertype === 'superadmin' || user?.usertype === 'admin') {
      // Check if user dismissed onboarding this session
      if (!sessionStorage.getItem('onboarding-dismissed')) {
        setShowOnboarding(true);
      }
    }
  }, []);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    sessionStorage.setItem('onboarding-dismissed', '1');
  };

  return (
    <div className="app-layout">
      {/* Fixed Header */}
      <TopNav onMenuClick={() => setSidebarShow(true)} />
      
      <div className="app-container">
        {/* Desktop Sidebar */}
        <div className="d-none d-md-block">
          <Sidebar show={true} onHide={() => {}} isOffcanvas={false} />
        </div>
        
        {/* Mobile Offcanvas Sidebar */}
        <Sidebar show={sidebarShow} onHide={() => setSidebarShow(false)} isOffcanvas={true} />
        
        {/* Main Content */}
        <div className={`main-content-wrapper${isChatPage ? ' no-padding' : ''}`}>
          <Outlet />
        </div>
      </div>

      {/* Onboarding Wizard — modal overlay for first-time setup */}
      <OnboardingWizard
        show={showOnboarding}
        onComplete={handleOnboardingComplete}
      />

      {/* Terms & Conditions — blocking modal for tenantadmin first-time */}
      {showTerms && (
        <TermsAgreementModal onAccepted={() => setShowTerms(false)} />
      )}

      {/* AI Doubt Chat — floating widget for students (plan-gated, hidden during practice/exams) */}
      {getCurrentUser()?.usertype === 'student' && !hideAIWidget && hasPlanFeature('aiDoubtSolving') && (
        <Suspense fallback={null}>
          <AIDoubtChatWidget />
        </Suspense>
      )}
    </div>
  );
};

export default Layout;
